// ============================================================================
// AGENT DASHBOARD - SPECIAL LINK GENERATOR
// Life & Wealth Solutions V10.X
// ============================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AgentDashboard = () => {
    const [agentAuth, setAgentAuth] = useState(null);
    const [links, setLinks] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showGenerator, setShowGenerator] = useState(false);

    // Form state for link generation
    const [formData, setFormData] = useState({
        recipientEmail: '',
        recipientName: '',
        recipientCompany: '',
        recipientPhone: '',
        industryType: 'life_insurance',
        region: '',
        stateCode: '',
        demoDurationHours: 168,
        sendEmail: true
    });

    const agentId = localStorage.getItem('agentId'); // From auth

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);

            // Load agent authorization
            const authRes = await axios.get(`/api/demo/agent/${agentId}/authorization`);
            setAgentAuth(authRes.data.authorization);

            // Load agent stats
            const statsRes = await axios.get(`/api/demo/special-links/agent/${agentId}/stats`);
            setStats(statsRes.data.stats);

            // Load recent links
            const linksRes = await axios.get(`/api/demo/special-links/agent/${agentId}`);
            setLinks(linksRes.data.links || []);

            setLoading(false);
        } catch (error) {
            console.error('Dashboard load error:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGenerateLink = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post('/api/demo/special-links/generate', formData);

            if (response.data.success) {
                alert(`✅ Special Link Generated!\n\nLink: ${response.data.specialLink.linkUrl}\n\n${
                    formData.sendEmail ? 'Email sent to recipient!' : 'Copy link to share manually.'
                }`);

                // Reset form
                setFormData({
                    recipientEmail: '',
                    recipientName: '',
                    recipientCompany: '',
                    recipientPhone: '',
                    industryType: 'life_insurance',
                    region: '',
                    stateCode: '',
                    demoDurationHours: 168,
                    sendEmail: true
                });

                setShowGenerator(false);
                loadDashboardData(); // Refresh data
            }
        } catch (error) {
            alert(`❌ Error: ${error.response?.data?.error || error.message}`);
        }
    };

    const copyLinkToClipboard = (linkUrl) => {
        navigator.clipboard.writeText(linkUrl);
        alert('✅ Link copied to clipboard!');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Agent Dashboard</h1>
                    <p className="text-gray-600 mt-2">Manage your demo links and track performance</p>
                </div>

                {/* Agent Authorization Card */}
                {agentAuth && (
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 mb-6 text-white">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-purple-200 text-sm">Agent Level</p>
                                <p className="text-2xl font-bold capitalize">{agentAuth.agentLevel}</p>
                            </div>
                            <div>
                                <p className="text-purple-200 text-sm">Monthly Quota</p>
                                <p className="text-2xl font-bold">
                                    {agentAuth.permissions.linksGeneratedThisMonth} / {agentAuth.permissions.maxLinksPerMonth || '∞'}
                                </p>
                            </div>
                            <div>
                                <p className="text-purple-200 text-sm">Commission Rate</p>
                                <p className="text-2xl font-bold">{agentAuth.commission.rate}%</p>
                            </div>
                            <div>
                                <p className="text-purple-200 text-sm">Total Earned</p>
                                <p className="text-2xl font-bold">${agentAuth.commission.totalEarned.toLocaleString()}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Performance Stats */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Total Links</p>
                                    <p className="text-3xl font-bold text-gray-900">{stats.total_links}</p>
                                </div>
                                <div className="bg-blue-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Demos Accessed</p>
                                    <p className="text-3xl font-bold text-gray-900">{stats.accessed_links}</p>
                                </div>
                                <div className="bg-green-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Conversions</p>
                                    <p className="text-3xl font-bold text-gray-900">{stats.license_conversions}</p>
                                </div>
                                <div className="bg-purple-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Conversion Rate</p>
                                    <p className="text-3xl font-bold text-gray-900">{stats.conversion_rate || 0}%</p>
                                </div>
                                <div className="bg-yellow-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Generate Link Button */}
                <div className="mb-6">
                    <button
                        onClick={() => setShowGenerator(!showGenerator)}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors flex items-center space-x-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Generate New Special Link</span>
                    </button>
                </div>

                {/* Link Generator Form */}
                {showGenerator && (
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Special Link</h2>
                        <form onSubmit={handleGenerateLink} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Recipient Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="recipientEmail"
                                        value={formData.recipientEmail}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Recipient Name
                                    </label>
                                    <input
                                        type="text"
                                        name="recipientName"
                                        value={formData.recipientName}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        placeholder="John Smith"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        name="recipientCompany"
                                        value={formData.recipientCompany}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        placeholder="ABC Insurance Agency"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="recipientPhone"
                                        value={formData.recipientPhone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Industry Type
                                    </label>
                                    <select
                                        name="industryType"
                                        value={formData.industryType}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                    >
                                        <option value="life_insurance">Life Insurance</option>
                                        <option value="p_c_insurance">P&C Insurance</option>
                                        <option value="wealth_management">Wealth Management</option>
                                        <option value="multi_line">Multi-Line</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State/Region
                                    </label>
                                    <input
                                        type="text"
                                        name="region"
                                        value={formData.region}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        placeholder="Georgia"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Demo Duration (hours)
                                    </label>
                                    <input
                                        type="number"
                                        name="demoDurationHours"
                                        value={formData.demoDurationHours}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="720"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{Math.round(formData.demoDurationHours / 24)} days</p>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="sendEmail"
                                        checked={formData.sendEmail}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Send Special Email automatically
                                    </label>
                                </div>
                            </div>

                            <div className="flex space-x-4 pt-4">
                                <button
                                    type="submit"
                                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                                >
                                    Generate Link
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowGenerator(false)}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-6 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Recent Links Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Recent Special Links</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Recipient
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Company
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {links.map((link) => (
                                    <tr key={link.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {link.recipient_name || 'N/A'}
                                                </div>
                                                <div className="text-sm text-gray-500">{link.recipient_email}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {link.recipient_company || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                link.status === 'active' ? 'bg-green-100 text-green-800' :
                                                link.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                                                link.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {link.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(link.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => copyLinkToClipboard(link.link_url)}
                                                className="text-purple-600 hover:text-purple-900 mr-3"
                                            >
                                                Copy Link
                                            </button>
                                            <a
                                                href={`/demo/analytics/${link.id}`}
                                                className="text-indigo-600 hover:text-indigo-900"
                                            >
                                                View Analytics
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentDashboard;
